import {
	DeliveryMethodBadges,
	IconButton,
	XIcon,
} from "@au-schein-de/au-ui-library";
import { type ReactNode, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type {
	MapCenter,
	PharmacyMarker,
	PharmacyMarkerAddress,
} from "./pharmacy-map.types";
import { PharmacyMapPanel } from "./pharmacy-map-panel.component";

type DeliveryMethod = "shipping" | "courier" | "pickup";

interface MobileFullscreenMapOverlayProps {
	isOpen: boolean;
	preload: boolean;
	center: MapCenter | null;
	pharmacyMarkers?: PharmacyMarker[];
	selectedPharmacyId?: number | null;
	onPharmacyMarkerClick?: (pharmacyId: number) => void;
	onDismissPreview?: () => void;
	onConfirmSelection?: () => void;
	onClose: () => void;
	hasVendorResults?: boolean;
	isLoading?: boolean;
	radiusControl?: ReactNode;
}

function formatDistance(distanceMeters: number): string {
	const km = distanceMeters / 1000;
	if (km < 1) return `~${Math.round(distanceMeters)}m`;
	if (km < 10) return `~${(Math.round(km * 10) / 10).toString()}km`;
	return `~${Math.round(km)}km`;
}

function formatMarkerAddress(addr: PharmacyMarkerAddress): string {
	const parts = [
		addr.street,
		[addr.postalCode, addr.city].filter(Boolean).join(" "),
	].filter(Boolean);
	return parts.join(", ");
}

export function MobileFullscreenMapOverlay({
	isOpen,
	preload,
	center,
	pharmacyMarkers,
	selectedPharmacyId,
	onPharmacyMarkerClick,
	onDismissPreview,
	onConfirmSelection,
	onClose,
	hasVendorResults = true,
	isLoading = false,
	radiusControl,
}: MobileFullscreenMapOverlayProps) {
	const { t } = useTranslation("product");
	const selectedPharmacy = useMemo(
		() => pharmacyMarkers?.find((m) => m.id === selectedPharmacyId) ?? null,
		[pharmacyMarkers, selectedPharmacyId],
	);
	const deliveryMethodsForBadges = useMemo(
		(): DeliveryMethod[] =>
			(selectedPharmacy?.deliveryMethods ?? []).filter(
				(m): m is DeliveryMethod =>
					m === "shipping" || m === "courier" || m === "pickup",
			),
		[selectedPharmacy],
	);
	const formattedAddress = useMemo(
		() =>
			selectedPharmacy?.address
				? formatMarkerAddress(selectedPharmacy.address)
				: null,
		[selectedPharmacy],
	);

	useEffect(() => {
		if (!isOpen) return;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [isOpen]);

	if ((!isOpen && !preload) || typeof document === "undefined") {
		return null;
	}

	return createPortal(
		<div
			className="fixed inset-0 z-[120] bg-white md:hidden"
			style={isOpen ? undefined : { visibility: "hidden" }}
			aria-hidden={!isOpen}
		>
			<div className="absolute inset-x-4 top-4 z-10 flex items-start justify-between gap-2">
				<div className="pointer-events-auto">{radiusControl}</div>
				<IconButton
					onClick={onClose}
					icon={<XIcon.Raw />}
					shape="circle"
					aria-label="Close map"
					className="bg-gray-50 shadow-md"
				/>
			</div>
			<div className="h-full w-full">
				<PharmacyMapPanel
					center={center}
					pharmacyMarkers={pharmacyMarkers}
					selectedPharmacyId={selectedPharmacyId}
					onPharmacyMarkerClick={isOpen ? onPharmacyMarkerClick : undefined}
					onDismissPreview={onDismissPreview}
					interactive={isOpen}
					navigationControlPosition="bottom-right"
					hasVendorResults={hasVendorResults}
					isLoading={isLoading}
				/>
			</div>
			{selectedPharmacy && (
				<div
					className="absolute inset-x-0 bottom-0 z-10"
					data-testid="mobile-pharmacy-card"
				>
					<div className="px-4 pb-3">
						<div className="rounded-xl border-2 border-ansay-500 bg-white px-4 py-3 shadow-lg">
							<div className="flex w-full flex-col items-start gap-2">
								<span className="w-full text-start text-body-bold text-ansay-700">
									{selectedPharmacy.name}
								</span>
								<div className="flex w-full flex-col gap-1.5">
									{(formattedAddress || selectedPharmacy.distance != null) && (
										<div className="flex items-center gap-1.5">
											{formattedAddress && (
												<span
													className="truncate text-body-sm text-gray-500"
													title={formattedAddress}
												>
													{formattedAddress}
												</span>
											)}
											{selectedPharmacy.distance != null && (
												<span
													className="whitespace-nowrap text-body-sm text-gray-700"
													data-testid="vendor-distance"
												>
													{formatDistance(selectedPharmacy.distance)}
												</span>
											)}
										</div>
									)}
									{deliveryMethodsForBadges.length > 0 && (
										<div data-delivery-badges="true">
											<DeliveryMethodBadges
												methods={deliveryMethodsForBadges}
												variant="pharmacy"
											/>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
					<div className="bg-white px-4 py-3">
						<button
							type="button"
							className="w-full rounded-xl bg-ansay-light p-4 text-body-emphasis text-gray-700 transition-opacity hover:opacity-90"
							onClick={onConfirmSelection}
							data-testid="pharmacy-map-confirm-cta-fullscreen"
						>
							{t("vendor.map.selectPharmacy")}
						</button>
					</div>
				</div>
			)}
		</div>,
		document.body,
	);
}
